import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    UserPlus, Trash2, RotateCcw, Ban, PowerOff, Power, Wallet,
    Mail, MailWarning, MailCheck, Send, FileSpreadsheet,
    GraduationCap, Users as UsersIcon, KeyRound, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import BulkActionBar from "@/components/BulkActionBar";
import ManualEnrollDialog from "@/components/admin/ManualEnrollDialog";
import ImportUsersCsvDialog from "@/components/admin/ImportUsersCsvDialog";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

/**
 * Relative time helper for the "Last sign-in" column. We deliberately
 * keep it coarse — admins want to spot "never signed in" or "stale" at a
 * glance, not down to the second.
 */
function relativeFromNow(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso).getTime();
        const diff = Date.now() - then;
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return formatDate(iso);
    } catch {
        return iso;
    }
}

export default function AdminUsers() {
    const { user: currentUser } = useAuth();
    const [activeUsers, setActiveUsers] = useState([]);
    const [trashedUsers, setTrashedUsers] = useState([]);
    const [filter, setFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [manualEnrollOpen, setManualEnrollOpen] = useState(false);
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    const [programs, setPrograms] = useState([]);

    useEffect(() => {
        api.get("/programs", { params: { include_archived: true } })
            .then((r) => {
                const list = (r.data || [])
                    .filter((p) => !p.is_deleted)
                    .sort((a, b) => a.title.localeCompare(b.title));
                setPrograms(list);
            })
            .catch(() => setPrograms([]));
    }, []);
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
    const [tab, setTab] = useState("active");
    const [confirmUser, setConfirmUser] = useState(null); // activate/deactivate
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [purgeTarget, setPurgeTarget] = useState(null);
    const [selectedActive, setSelectedActive] = useState(new Set());
    const [selectedTrash, setSelectedTrash] = useState(new Set());
    const [bulkConfirm, setBulkConfirm] = useState(null);

    const load = useCallback(async () => {
        const [a, t] = await Promise.all([
            api.get("/admin/users"),
            api.get("/admin/users?include_deleted=true"),
        ]);
        setActiveUsers(a.data);
        setTrashedUsers(t.data);
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        setSelectedActive(new Set());
        setSelectedTrash(new Set());
    }, [tab]);

    const filterFn = (arr) =>
        arr.filter(
            (u) =>
                !filter ||
                u.name.toLowerCase().includes(filter.toLowerCase()) ||
                u.email.toLowerCase().includes(filter.toLowerCase()) ||
                u.role === filter,
        );
    const filteredActive = filterFn(activeUsers);
    const filteredTrash = filterFn(trashedUsers);

    // Split active users into Staff (admin/instructor/sme) and Students so
    // the admin can keep them visually separated. Anything that isn't a
    // student is treated as staff so any future roles default to the visible
    // staff group rather than disappearing.
    const filteredStaff = filteredActive.filter((u) => u.role !== "student");
    const filteredStudents = filteredActive.filter((u) => u.role === "student");

    const toggleSel = (set, setter, id) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setter(next);
    };
    const toggleAll = (items, set, setter) => {
        const selectable = items.filter((u) => u.id !== currentUser?.id);
        if (set.size === selectable.length) setter(new Set());
        else setter(new Set(selectable.map((u) => u.id)));
    };

    const createUser = async () => {
        try {
            const { data } = await api.post("/admin/users", form);
            // The /admin/users endpoint sets `setup_email_sent` when Resend
            // accepted the message and returned a message id. If Resend
            // rejected it (quota exhausted, unverified sender, missing key,
            // etc) the endpoint sets `setup_email_error` instead — surface
            // the actual reason so admins can act on it instead of trusting
            // a misleading "email sent" green toast.
            if (data?.setup_email_sent) {
                toast.success(
                    `${data.email} created. Setup email sent — they'll choose their own password.`,
                );
            } else if (data?.setup_email_error) {
                toast.error(
                    `${data.email} was created, but the setup email FAILED to send: ${data.setup_email_error}. Use the "Resend setup link" button on their dossier once the issue is fixed.`,
                    { duration: 12000 },
                );
            } else {
                toast.success(
                    `${data.email} created with the password you set.`,
                );
            }
            setOpen(false);
            setForm({ name: "", email: "", password: "", role: "student" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const updateRole = async (userId, role) => {
        try {
            await api.patch(`/admin/users/${userId}`, { role });
            toast.success("Role updated");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Role update failed");
        }
    };
    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
            toast.success(u.is_active ? `${u.name} deactivated` : `${u.name} activated`);
            setConfirmUser(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
            setConfirmUser(null);
        }
    };

    const singleAction = async (url, method, successMsg) => {
        try {
            await api.request({ url, method });
            toast.success(successMsg);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    /**
     * Re-send the password-setup link for a user whose initial email was
     * rejected by Resend (quota / unverified-sender / etc). The button is
     * only shown when the user's `setup_email_status === "failed"` so the
     * action is always meaningful — no point spamming users whose setup
     * email was successfully delivered the first time.
     */
    const resendSetupLink = async (u) => {
        try {
            await api.post(`/admin/users/${u.id}/send-password-reset`);
            toast.success(`Password reset link emailed to ${u.email}.`);
            load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not send the reset link.",
                { duration: 9000 },
            );
        }
    };

    const loginAsStudent = async (u) => {
        const fmtError = (e, fb) => e?.response?.data?.detail || e?.message || fb;
        if (!window.confirm(
            `Open the student dashboard as ${u.email}? Your admin session will resume when you sign back out.`,
        )) return;
        try {
            const { data } = await api.post(`/admin/users/${u.id}/impersonation-token`);
            // Stash the current admin auth so we can restore it later
            const currentToken = localStorage.getItem("token");
            sessionStorage.setItem("hccbc_admin_resume", JSON.stringify({
                admin_token: currentToken,
                impersonation_started_at: new Date().toISOString(),
                target_email: data.student.email,
                target_id: data.student.id,
            }));
            // Switch to student session
            localStorage.setItem("token", data.token);
            toast.success(`Now signed in as ${data.student.email}`);
            window.location.href = "/dashboard";
        } catch (e) {
            toast.error(fmtError(e, "Could not impersonate user"));
        }
    };

    const runBulk = async () => {
        if (!bulkConfirm) return;
        const ids = Array.from(
            bulkConfirm.scope === "trash" ? selectedTrash : selectedActive,
        );
        try {
            const { data } = await api.post("/admin/users/bulk", {
                ids,
                action: bulkConfirm.action,
            });
            if (data.failed) toast.warning(`${data.ok} succeeded, ${data.failed} failed.`);
            else toast.success(`${data.ok} user(s) updated.`);
            setSelectedActive(new Set());
            setSelectedTrash(new Set());
            setBulkConfirm(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk action failed");
            setBulkConfirm(null);
        }
    };

    const allActiveSelected = useMemo(() => {
        const selectable = filteredActive.filter((u) => u.id !== currentUser?.id);
        return selectable.length > 0 && selectedActive.size === selectable.length;
    }, [filteredActive, selectedActive, currentUser]);
    const allTrashSelected = useMemo(
        () => filteredTrash.length > 0 && selectedTrash.size === filteredTrash.length,
        [filteredTrash, selectedTrash],
    );

    const renderActiveRow = (u) => {
        const isSelf = u.id === currentUser?.id;
        const checked = selectedActive.has(u.id);
        return (
            <TableRow
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className={checked ? "bg-accent/5" : ""}
            >
                <TableCell className="w-10">
                    <Checkbox
                        checked={checked}
                        disabled={isSelf}
                        onCheckedChange={() =>
                            !isSelf && toggleSel(selectedActive, setSelectedActive, u.id)
                        }
                        data-testid={`check-user-${u.id}`}
                        aria-label={`Select ${u.name}`}
                    />
                </TableCell>
                <TableCell className="font-medium">
                    {u.name}
                    {isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                    <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger
                            className="w-32 h-8"
                            data-testid={`role-select-${u.id}`}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="instructor">Instructor</SelectItem>
                            <SelectItem value="sme">SME (Subject-matter expert)</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                    </Select>
                </TableCell>
                <TableCell>
                    {u.is_active ? (
                        <Badge className="bg-accent/20 text-accent border border-accent/40">
                            Active
                        </Badge>
                    ) : (
                        <Badge variant="outline">Disabled</Badge>
                    )}
                </TableCell>
                <TableCell
                    className="text-xs"
                    data-testid={`last-signin-${u.id}`}
                    title={u.last_seen_at || ""}
                >
                    {u.last_seen_at ? (
                        <span className="text-foreground">{relativeFromNow(u.last_seen_at)}</span>
                    ) : (
                        <span className="text-amber-600 font-medium">Never signed in</span>
                    )}
                </TableCell>
                <TableCell
                    className="text-xs"
                    data-testid={`setup-progress-${u.id}`}
                    title={
                        u.password_set_at
                            ? `Setup link clicked: ${u.setup_link_clicked_at || "—"}\nPassword set: ${u.password_set_at}`
                            : u.setup_link_clicked_at
                            ? `Setup link clicked: ${u.setup_link_clicked_at}\nPassword: not yet set`
                            : "Setup link has never been opened by this user."
                    }
                >
                    {u.password_set_at ? (
                        <span className="text-emerald-700">
                            ✓ Set {relativeFromNow(u.password_set_at)}
                        </span>
                    ) : u.setup_link_clicked_at ? (
                        <span className="text-amber-700">
                            ⚠ Opened, not finished
                        </span>
                    ) : (
                        <span className="text-muted-foreground">
                            Link never opened
                        </span>
                    )}
                </TableCell>
                <TableCell
                    className="text-xs"
                    data-testid={`setup-email-${u.id}`}
                    title={u.setup_email_error || ""}
                >
                    {u.setup_email_status === "sent" ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            <MailCheck className="h-3 w-3 mr-1" />
                            Sent
                        </Badge>
                    ) : u.setup_email_status === "failed" ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">
                            <MailWarning className="h-3 w-3 mr-1" />
                            Failed
                        </Badge>
                    ) : (
                        <span className="text-muted-foreground">—</span>
                    )}
                </TableCell>
                <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                        {u.setup_email_status === "failed" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resendSetupLink(u)}
                                data-testid={`resend-setup-${u.id}`}
                                className="text-primary border-primary/40 hover:bg-primary/10"
                                title={`Resend setup link to ${u.email}`}
                            >
                                <Send className="h-3 w-3 mr-1" />
                                Resend link
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resendSetupLink(u)}
                            data-testid={`send-reset-${u.id}`}
                            className="text-[#C79520] border-[#C79520]/50 hover:bg-[#C79520]/10"
                            title={`Email a password reset link to ${u.email}`}
                            disabled={isSelf}
                        >
                            <KeyRound className="h-3 w-3 mr-1" />
                            Reset password
                        </Button>
                        {u.role === "student" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => loginAsStudent(u)}
                                data-testid={`login-as-${u.id}`}
                                className="text-[#134B30] border-[#134B30]/40 hover:bg-[#134B30]/10"
                                title={`Open dashboard as ${u.email}`}
                                disabled={isSelf}
                            >
                                <UserCheck className="h-3 w-3 mr-1" />
                                Login as
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmUser(u)}
                            disabled={isSelf}
                            title={isSelf ? "You cannot change your own account status" : ""}
                            data-testid={`toggle-active-${u.id}`}
                        >
                            {u.is_active ? (
                                <>
                                    <PowerOff className="h-3 w-3 mr-1" />
                                    Deactivate
                                </>
                            ) : (
                                <>
                                    <Power className="h-3 w-3 mr-1" />
                                    Activate
                                </>
                            )}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => setDeleteTarget(u)}
                            disabled={isSelf}
                            title={isSelf ? "You cannot delete your own account" : ""}
                            data-testid={`delete-user-${u.id}`}
                        >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                        </Button>
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    const renderTrashRow = (u) => {
        const checked = selectedTrash.has(u.id);
        return (
            <TableRow
                key={u.id}
                data-testid={`trash-user-row-${u.id}`}
                className={checked ? "bg-destructive/5" : ""}
            >
                <TableCell className="w-10">
                    <Checkbox
                        checked={checked}
                        onCheckedChange={() =>
                            toggleSel(selectedTrash, setSelectedTrash, u.id)
                        }
                        data-testid={`check-trash-user-${u.id}`}
                        aria-label={`Select ${u.name}`}
                    />
                </TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell className="capitalize">{u.role}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                    {formatDate(u.deleted_at)}
                </TableCell>
                <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                        <Button
                            size="sm"
                            onClick={() => setRestoreTarget(u)}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`restore-user-${u.id}`}
                        >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restore
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => setPurgeTarget(u)}
                            data-testid={`purge-user-${u.id}`}
                        >
                            <Ban className="h-3 w-3 mr-1" />
                            Delete forever
                        </Button>
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 pb-24" data-testid="admin-users-page">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-3 anim-fade-up">
                <div>
                    <div className="inline-flex items-center gap-1.5 chip chip-accent">
                        <UserPlus className="h-3 w-3" />
                        People
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-primary mt-3 tracking-tight">
                        User Management
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                        Manage students, instructors and admins. Walk-ins paying offline get a one-tap enrolment.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Input
                        placeholder="Search name, email, role…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-64"
                        data-testid="users-filter-input"
                    />
                    <Button
                        variant="outline"
                        onClick={() => setManualEnrollOpen(true)}
                        className="gap-1.5 border-accent/40 text-accent-foreground hover:bg-accent/15"
                        data-testid="manual-enroll-open-btn"
                    >
                        <Wallet className="h-4 w-4" />
                        Enrol walk-in
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setCsvImportOpen(true)}
                        className="gap-1.5"
                        data-testid="csv-import-open-btn"
                    >
                        <FileSpreadsheet className="h-4 w-4" />
                        Import CSV
                    </Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-primary hover:bg-primary/90"
                                data-testid="add-user-btn"
                            >
                                <UserPlus className="h-4 w-4 mr-2" />
                                Add user
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display">New user</DialogTitle>
                                <DialogDescription>
                                    Leave the password blank to email a setup link, or set
                                    one now to share manually. Staff (instructor, SME, admin)
                                    can log in from any portal page.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label>Name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        data-testid="new-user-name"
                                    />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        data-testid="new-user-email"
                                    />
                                </div>
                                <div>
                                    <Label className="flex items-baseline justify-between">
                                        <span>Password</span>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
                                            optional
                                        </span>
                                    </Label>
                                    <Input
                                        type="password"
                                        value={form.password}
                                        onChange={(e) =>
                                            setForm({ ...form, password: e.target.value })
                                        }
                                        data-testid="new-user-password"
                                        placeholder={
                                            form.role === "student"
                                                ? "Leave blank to email a setup link"
                                                : "Leave blank — we'll email a setup link"
                                        }
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        {form.role === "student"
                                            ? "Students normally set their own password via the setup email."
                                            : "Recommended for staff: leave blank so they receive an email to set their own password."}
                                    </p>
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <Select
                                        value={form.role}
                                        onValueChange={(v) => setForm({ ...form, role: v })}
                                    >
                                        <SelectTrigger data-testid="new-user-role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="student">Student</SelectItem>
                                            <SelectItem value="instructor">Instructor</SelectItem>
                                            <SelectItem value="sme">SME</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={createUser}
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="create-user-submit"
                                >
                                    Create
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="active" data-testid="tab-active-users">
                        Active
                        <Badge variant="outline" className="ml-2">
                            {activeUsers.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="trash" data-testid="tab-trash-users">
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Trash
                        <Badge variant="outline" className="ml-2">
                            {trashedUsers.length}
                        </Badge>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="mt-6">
                    <Accordion type="multiple" defaultValue={["staff", "students"]} className="space-y-3">
                        {/* ------------------------------------------- STAFF */}
                        <AccordionItem value="staff" className="border-0">
                            <Card className="border-border overflow-hidden">
                                <AccordionTrigger className="px-4 py-3 hover:bg-muted/40 hover:no-underline" data-testid="users-staff-trigger">
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                            <UsersIcon className="h-4 w-4" />
                                        </div>
                                        <div className="text-left">
                                            <div className="font-display text-base text-primary">Staff</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                Admins, instructors, and subject-matter experts
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="ml-2">
                                            {filteredStaff.length}
                                        </Badge>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="p-0">
                                    <CardContent className="p-0">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10">
                                                        <Checkbox
                                                            checked={allActiveSelected}
                                                            onCheckedChange={() =>
                                                                toggleAll(
                                                                    filteredActive,
                                                                    selectedActive,
                                                                    setSelectedActive,
                                                                )
                                                            }
                                                            data-testid="select-all-users-staff"
                                                            aria-label="Select all staff"
                                                        />
                                                    </TableHead>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Role</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Last sign-in</TableHead>
                                                    <TableHead>Password setup</TableHead>
                                                    <TableHead>Setup email</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredStaff.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={9}
                                                            className="text-center text-muted-foreground py-8"
                                                        >
                                                            No staff match your search.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    filteredStaff.map(renderActiveRow)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>

                        {/* ---------------------------------------- STUDENTS */}
                        <AccordionItem value="students" className="border-0">
                            <Card className="border-border overflow-hidden">
                                <AccordionTrigger className="px-4 py-3 hover:bg-muted/40 hover:no-underline" data-testid="users-students-trigger">
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-[#C79520]/15 text-[#C79520] flex items-center justify-center">
                                            <GraduationCap className="h-4 w-4" />
                                        </div>
                                        <div className="text-left">
                                            <div className="font-display text-base text-primary">Students</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                Enrolled learners across all programs
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="ml-2">
                                            {filteredStudents.length}
                                        </Badge>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="p-0">
                                    <CardContent className="p-0">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10" />
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Role</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Last sign-in</TableHead>
                                                    <TableHead>Password setup</TableHead>
                                                    <TableHead>Setup email</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredStudents.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={9}
                                                            className="text-center text-muted-foreground py-8"
                                                        >
                                                            No students match your search.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    filteredStudents.map(renderActiveRow)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                    </Accordion>
                </TabsContent>

                <TabsContent value="trash" className="mt-6">
                    <p className="text-sm text-muted-foreground mb-4">
                        Trashed users cannot log in. Restore to re-enable access, or permanently
                        delete to also remove their enrollments, progress, quiz attempts and
                        certificates. Programs they authored are left untouched.
                    </p>
                    <Card className="border-border">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10">
                                            <Checkbox
                                                checked={allTrashSelected}
                                                onCheckedChange={() =>
                                                    toggleAll(
                                                        filteredTrash,
                                                        selectedTrash,
                                                        setSelectedTrash,
                                                    )
                                                }
                                                data-testid="select-all-users-trash"
                                                aria-label="Select all trashed users"
                                            />
                                        </TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Deleted</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTrash.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center text-muted-foreground py-8"
                                            >
                                                Trash is empty.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredTrash.map(renderTrashRow)
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {tab === "active" && (
                <BulkActionBar
                    testId="bulk-bar-users-active"
                    count={selectedActive.size}
                    onClear={() => setSelectedActive(new Set())}
                    actions={[
                        {
                            key: "activate",
                            label: "Activate",
                            Icon: Power,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "active",
                                    action: "activate",
                                    title: "Activate selected users?",
                                    desc: `${selectedActive.size} user(s) will regain portal access.`,
                                }),
                        },
                        {
                            key: "deactivate",
                            label: "Deactivate",
                            Icon: PowerOff,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "active",
                                    action: "deactivate",
                                    title: "Deactivate selected users?",
                                    desc: `${selectedActive.size} user(s) will be unable to log in until reactivated. Existing enrollments and certificates are preserved.`,
                                }),
                        },
                        {
                            key: "delete",
                            label: "Move to trash",
                            Icon: Trash2,
                            destructive: true,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "active",
                                    action: "delete",
                                    title: "Move selected users to trash?",
                                    desc: `${selectedActive.size} user(s) will be deactivated and moved to the Trash tab. They cannot log in, and you can restore them at any time.`,
                                    destructive: true,
                                }),
                        },
                    ]}
                />
            )}

            {tab === "trash" && (
                <BulkActionBar
                    testId="bulk-bar-users-trash"
                    count={selectedTrash.size}
                    onClear={() => setSelectedTrash(new Set())}
                    actions={[
                        {
                            key: "restore",
                            label: "Restore",
                            Icon: RotateCcw,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "trash",
                                    action: "restore",
                                    title: "Restore selected users?",
                                    desc: `${selectedTrash.size} user(s) will be restored and reactivated. They can log in immediately.`,
                                }),
                        },
                        {
                            key: "purge",
                            label: "Delete forever",
                            Icon: Ban,
                            destructive: true,
                            onClick: () =>
                                setBulkConfirm({
                                    scope: "trash",
                                    action: "purge",
                                    title: "Delete these users forever?",
                                    desc: `${selectedTrash.size} user(s) and their enrollments, progress, quiz attempts and certificates will be permanently removed. Programs they authored as instructor are left intact. This cannot be undone.`,
                                    destructive: true,
                                }),
                        },
                    ]}
                />
            )}

            {/* Bulk confirm */}
            <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
                <AlertDialogContent data-testid="users-bulk-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            className={`font-display ${
                                bulkConfirm?.destructive ? "text-destructive" : ""
                            }`}
                        >
                            {bulkConfirm?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{bulkConfirm?.desc}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runBulk}
                            className={
                                bulkConfirm?.destructive
                                    ? "bg-destructive hover:bg-destructive/90"
                                    : "bg-primary hover:bg-primary/90"
                            }
                            data-testid="users-bulk-confirm-btn"
                        >
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Single-item dialogs */}
            <AlertDialog open={!!confirmUser} onOpenChange={(o) => !o && setConfirmUser(null)}>
                <AlertDialogContent data-testid="confirm-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            {confirmUser?.is_active ? "Deactivate user?" : "Activate user?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmUser?.is_active ? (
                                <>
                                    <span className="font-medium text-foreground">
                                        {confirmUser?.name}
                                    </span>{" "}
                                    ({confirmUser?.email}) will be unable to log in. Existing
                                    enrollments and certificates are preserved.
                                </>
                            ) : (
                                <>
                                    <span className="font-medium text-foreground">
                                        {confirmUser?.name}
                                    </span>{" "}
                                    ({confirmUser?.email}) will regain portal access.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-user-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => confirmUser && toggleActive(confirmUser)}
                            className={
                                confirmUser?.is_active
                                    ? "bg-destructive hover:bg-destructive/90"
                                    : "bg-primary hover:bg-primary/90"
                            }
                            data-testid="confirm-user-confirm"
                        >
                            {confirmUser?.is_active ? "Yes, deactivate" : "Yes, activate"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            Move this user to trash?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                {deleteTarget?.name}
                            </span>{" "}
                            ({deleteTarget?.email}) will be deactivated and moved to the Trash
                            tab. They will not be able to log in, and you can restore them at any
                            time.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (deleteTarget) {
                                    singleAction(
                                        `/admin/users/${deleteTarget.id}`,
                                        "delete",
                                        "Moved to trash",
                                    );
                                    setDeleteTarget(null);
                                }
                            }}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="delete-user-confirm-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Move to trash
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
                <AlertDialogContent data-testid="restore-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            Restore this user?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                {restoreTarget?.name}
                            </span>{" "}
                            ({restoreTarget?.email}) will be reactivated and can log in again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (restoreTarget) {
                                    singleAction(
                                        `/admin/users/${restoreTarget.id}/restore`,
                                        "post",
                                        "User restored",
                                    );
                                    setRestoreTarget(null);
                                }
                            }}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="restore-user-confirm-btn"
                        >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Yes, restore
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
                <AlertDialogContent data-testid="purge-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-destructive">
                            Delete this user forever?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                {purgeTarget?.name}
                            </span>{" "}
                            ({purgeTarget?.email}) and their enrollments, progress, quiz attempts
                            and certificates will be permanently removed. Programs they authored
                            as instructor are left intact.{" "}
                            <strong className="text-destructive">This cannot be undone.</strong>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (purgeTarget) {
                                    singleAction(
                                        `/admin/users/${purgeTarget.id}/purge`,
                                        "delete",
                                        "User permanently deleted",
                                    );
                                    setPurgeTarget(null);
                                }
                            }}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="purge-user-confirm-btn"
                        >
                            <Ban className="h-4 w-4 mr-2" />
                            Delete forever
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ManualEnrollDialog
                open={manualEnrollOpen}
                onOpenChange={setManualEnrollOpen}
                programs={programs}
                onDone={() => load()}
            />

            <ImportUsersCsvDialog
                open={csvImportOpen}
                onOpenChange={setCsvImportOpen}
                onImported={() => load()}
            />
        </div>
    );
}
